import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass, createSlots as _createSlots } from "vue"

const _hoisted_1 = { class: "container" }
const _hoisted_2 = { class: "content" }
const _hoisted_3 = { class: "content__description__name" }
const _hoisted_4 = {
  key: 0,
  class: "content__disabled-drop-zone"
}
const _hoisted_5 = {
  key: 4,
  class: "content__file-list"
}
const _hoisted_6 = ["file"]
const _hoisted_7 = { class: "content__file-list__row__text" }
const _hoisted_8 = { class: "content__file-list__row__status" }
const _hoisted_9 = {
  key: 0,
  class: "content__file-list__row__status__loading-spinner"
}
const _hoisted_10 = ["onClick"]
const _hoisted_11 = { class: "footer" }

import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import ButtonPrimary from '@/design-system/atoms/buttons/ButtonPrimary.vue'
import IconCheck from '@/design-system/assets/icons/IconCheck.vue'
import IconX from '@/design-system/assets/icons/IconX.vue'
import InputFileDrop from './InputFileDrop.vue'
import PulseLoader from 'vue-spinner/src/PulseLoader.vue'
import useFileList from '@/hooks/attachments/useFileListNew'
import ModalBase from './ModalBase.vue'
import ButtonOutline from '@/design-system/atoms/buttons/ButtonOutline.vue'
import ButtonText from '@/design-system/atoms/buttons/ButtonText.vue'
import IconFileSmall from '@/design-system/assets/icons/IconFileSmall.vue'
import TextBase from '@/design-system/atoms/TextBase.vue'
import { DirectoryItem } from './types'
import IconFolderSmall from '@/design-system/assets/icons/IconFolderSmall.vue'
import DirectoryListView from './list-view/DirectoryListView.vue'
import BreadcrumbFiles from './BreadcrumbFiles.vue'
import BadgeSquare from '@/design-system/atoms/BadgeSquare.vue'
import { postDirectoryItem, replaceDirectoryItem } from './files.service'
import { AxiosError } from 'axios'
import FileConflictModal from './FileConflictModal.vue'

type Props = {
  currentDirectory: DirectoryItem<'directory'>
  open?: boolean
  fileEntries?: FileSystemEntry[]
}

type State = 'selecting' | 'uploading' | 'confirming'

type ConflictFile = {
  item: DirectoryItem
  suggestedName: string
}

// emits
interface EMITS {
  (e: 'close'): void
  (e: 'save'): void
}

export default /*@__PURE__*/_defineComponent({
  __name: 'UploadModal',
  props: {
    currentDirectory: {},
    open: { type: Boolean, default: false },
    fileEntries: { default: () => [] }
  },
  emits: ["close", "save"],
  setup(__props: any, { emit: __emit }) {

const props = __props

const emits = __emit

// variables
const { t } = useI18n()
const { tree, addFiles, addEntries, removeItem, fileManager } = useFileList()

const isOpen = ref<boolean>(props.open)
const state = ref<State>('selecting')
const showAdvancedView = ref<boolean>(false)
const conflictFiles = ref<ConflictFile[]>([])

// computed
const isUploadingFiles = computed(() => {
  return tree.tree.value.children.some((file) => file.status === 'loading')
})

const isFileInputDisabled = computed(() => {
  return isUploadingFiles.value || state.value !== 'selecting' || !showInputFileDrop.value
})
const showInputFileDrop = computed(() => {
  return true
})
const showFileView = computed(() => {
  return tree.tree.value.children.length > 0
})

watch(
  () => props.fileEntries,
  (newEntries) => {
    if (newEntries && newEntries.length > 0) {
      addEntries(newEntries)
    }
  },
  { immediate: true }
)

// methods
function closeModal() {
  isOpen.value = false
  emits('close')
}
function onInputChange(files: File[]) {
  addFiles(files)
}

async function uploadRecursively(
  item: DirectoryItem,
  parentDirectoryId: string = props.currentDirectory.id
) {
  try {
    const saved = await postDirectoryItem({
      ...item,
      directoryId: parentDirectoryId
    })

    if (item.type === 'directory' && item.children?.length) {
      await Promise.all(item.children.map((child) => uploadRecursively(child, saved.id)))
    }
    tree.updateNodeDetails(item.id, {
      id: saved.id,
      status: true
    })
  } catch (error) {
    console.error('Error uploading item:', item.name, error)
    const axiosError = error as AxiosError
    if (axiosError.response?.status === 409) {
      const suggestedName =
        (axiosError.response.data as { suggestedName: string }).suggestedName ??
        `${item.name} (copy)`
      conflictFiles.value.push({
        item,
        suggestedName: suggestedName
      })
    } else {
      tree.updateNodeDetails(item.id, {
        status: false
      })
    }
  }
}

async function uploadFiles() {
  const files = tree.tree.value.children
  if (files.length) {
    files.forEach((file) => {
      tree.updateNodeDetails(file.id, {
        status: 'loading'
      })
    })
    await Promise.all(
      files.map((file) => {
        return uploadRecursively(file)
      })
    )
    state.value = 'confirming' // Change state to confirming after upload
  }
}

function submitModal() {
  emits('save')
}

function handleKeyDown(event: KeyboardEvent) {
  if (event.ctrlKey && event.altKey && event.key === 'a') {
    event.preventDefault()
    showAdvancedView.value = !showAdvancedView.value
  } else if (event.key === 'Escape') {
    closeModal()
  }
}

watch(
  () => props.open,
  (newValue) => {
    isOpen.value = newValue
    if (newValue) {
      window.addEventListener('keydown', handleKeyDown)
    } else {
      window.removeEventListener('keydown', handleKeyDown)
      tree.resetTree()
      showAdvancedView.value = false
      state.value = 'selecting'
    }
  },
  { immediate: true }
)

function handleFilesDropped(items: DataTransferItem[]) {
  const entries = items
    .map((item) => item.webkitGetAsEntry())
    .filter((entry) => entry) as FileSystemEntry[]
  addEntries(entries)
}

async function onBreadcrumbClick(option: DirectoryItem) {
  if (!option.id) {
    console.warn('Breadcrumb has no id—skipping navigation', option)
    return
  }
  await fileManager.openItem(option.id)
}

async function handleAction(action: string, items: DirectoryItem[]) {
  fileManager.executeCommand({
    action,
    items
  })
  if (items.length === 0) {
    console.warn('Row action triggered without an item:', action)
    return
  }
}

async function replaceFile({ item }: ConflictFile) {
  try {
    await replaceDirectoryItem(item)
    tree.updateNodeDetails(item.id, {
      status: true
    })
  } catch (e) {
    console.error('Error replacing file:', item.name, e)
    tree.updateNodeDetails(item.id, {
      status: false
    })
    return
  }
}

async function saveCopy({ item, suggestedName }: ConflictFile) {
  try {
    tree.updateNodeDetails(item.id, {
      name: suggestedName,
      status: true
    })
    await postDirectoryItem({
      ...{
        ...item,
        id: undefined
      },
      name: suggestedName,
      directoryId: props.currentDirectory.id
    })
    tree.updateNodeDetails(item.id, {
      status: true
    })
  } catch (e) {
    console.error('Error saving copy of file:', item.name, e)
    tree.updateNodeDetails(item.id, {
      status: false
    })
    return
  }
}

function removeFile({ item }: ConflictFile) {
  conflictFiles.value = conflictFiles.value.filter((file) => file.item.id !== item.id)
  tree.updateNodeDetails(item.id, {
    status: false
  })
}

return (_ctx: any,_cache: any) => {
  const _component_i18n_t = _resolveComponent("i18n-t")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(ModalBase, {
      title: _unref(t)('general__data__file-upload-v2'),
      open: isOpen.value,
      onClose: closeModal
    }, _createSlots({
      content: _withCtx(() => [
        _createElementVNode("div", _hoisted_1, [
          _createElementVNode("div", _hoisted_2, [
            _createVNode(_component_i18n_t, {
              keypath: "general__text__file-upload-description",
              tag: "p",
              class: "content__description"
            }, {
              name: _withCtx(() => [
                _createElementVNode("span", _hoisted_3, _toDisplayString(props.currentDirectory.name), 1)
              ]),
              _: 1
            }),
            _createVNode(TextBase, {
              text: _unref(t)('general__text__file-move-later'),
              type: "label"
            }, null, 8, ["text"]),
            (!showInputFileDrop.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                  _createVNode(PulseLoader, {
                    loading: true,
                    class: "spinner",
                    color: "#1d234f"
                  }),
                  _cache[3] || (_cache[3] = _createElementVNode("p", { class: "content__disabled-drop-zone__error-text" }, "Message", -1))
                ]))
              : _createCommentVNode("", true),
            (showInputFileDrop.value)
              ? (_openBlock(), _createBlock(InputFileDrop, {
                  key: 1,
                  class: "content__drop-zone",
                  tabindex: "0",
                  disabled: isFileInputDisabled.value,
                  onFiles: onInputChange,
                  onItems: handleFilesDropped
                }, null, 8, ["disabled"]))
              : _createCommentVNode("", true),
            (showFileView.value && showAdvancedView.value)
              ? (_openBlock(), _createBlock(BreadcrumbFiles, {
                  key: 2,
                  breadcrumbs: _unref(fileManager).breadcrumbs.value,
                  onOnClicked: onBreadcrumbClick
                }, null, 8, ["breadcrumbs"]))
              : _createCommentVNode("", true),
            (showFileView.value && showAdvancedView.value)
              ? (_openBlock(), _createBlock(DirectoryListView, {
                  key: 3,
                  directoryItems: _unref(fileManager).displayedItems.value,
                  actions: [
              {
                actionName: 'delete',
                icon: 'IconTrash2Medium',
                labelKey: 'general__text__delete',
                appliesOn: ['directory', 'file', 'link']
              }
            ],
                  "disabled-types": ['link'],
                  onDoubleClick: _unref(fileManager).openItem,
                  onAction: handleAction
                }, null, 8, ["directoryItems", "onDoubleClick"]))
              : _createCommentVNode("", true),
            (showFileView.value && !showAdvancedView.value)
              ? (_openBlock(), _createElementBlock("ul", _hoisted_5, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(tree).tree.value.children, (item) => {
                    return (_openBlock(), _createElementBlock("li", {
                      key: item.id,
                      class: _normalizeClass(["content__file-list__row", { 'content__file-list__row__error': item.status === false }]),
                      file: item
                    }, [
                      (item.type === 'file')
                        ? (_openBlock(), _createBlock(IconFileSmall, {
                            key: 0,
                            class: "content__file-list__row__icon"
                          }))
                        : (item.type === 'directory')
                          ? (_openBlock(), _createBlock(IconFolderSmall, {
                              key: 1,
                              class: "content__file-list__row__icon"
                            }))
                          : _createCommentVNode("", true),
                      _createElementVNode("span", _hoisted_7, _toDisplayString(item.name), 1),
                      _createElementVNode("span", _hoisted_8, [
                        (item.status === 'loading')
                          ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
                              _createVNode(PulseLoader, {
                                color: "#3f46d6",
                                size: "8px"
                              })
                            ]))
                          : _createCommentVNode("", true),
                        (item.status === true)
                          ? (_openBlock(), _createBlock(IconCheck, {
                              key: 1,
                              class: "content__file-list__row__status__icon-success"
                            }))
                          : _createCommentVNode("", true),
                        (item.status === null)
                          ? (_openBlock(), _createBlock(ButtonText, {
                              key: 2,
                              iconLeft: require('@/design-system/assets/icons/Icon-X-Small.svg'),
                              size: "extra-small-icon",
                              onOnClicked: ($event: any) => (_unref(removeItem)(item))
                            }, null, 8, ["iconLeft", "onOnClicked"]))
                          : _createCommentVNode("", true),
                        (item.status === false)
                          ? (_openBlock(), _createElementBlock("button", {
                              key: 3,
                              class: "content__file-list__row__status__button",
                              "aria-label": "Remove failed file",
                              onClick: ($event: any) => (_unref(removeItem)(item))
                            }, [
                              _createVNode(IconX, { class: "content__file-list__row__status__button__icon-error" })
                            ], 8, _hoisted_10))
                          : _createCommentVNode("", true)
                      ])
                    ], 10, _hoisted_6))
                  }), 128))
                ]))
              : _createCommentVNode("", true)
          ])
        ])
      ]),
      footer: _withCtx(() => [
        _createElementVNode("div", _hoisted_11, [
          (state.value === 'selecting' && _unref(tree).tree.value.children.length > 0)
            ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                _createVNode(ButtonOutline, {
                  class: "footer__button",
                  buttonText: _unref(t)('general__text__cancel'),
                  disabled: isUploadingFiles.value,
                  onOnClicked: _cache[0] || (_cache[0] = ($event: any) => (closeModal()))
                }, null, 8, ["buttonText", "disabled"]),
                _createVNode(ButtonPrimary, {
                  class: "footer__button",
                  buttonText: _unref(t)('general__text__upload'),
                  disabled: isUploadingFiles.value,
                  onOnClicked: _cache[1] || (_cache[1] = ($event: any) => (uploadFiles()))
                }, null, 8, ["buttonText", "disabled"])
              ], 64))
            : (state.value === 'confirming')
              ? (_openBlock(), _createBlock(ButtonPrimary, {
                  key: 1,
                  class: "footer__button",
                  buttonText: _unref(t)('general__text__done'),
                  disabled: isUploadingFiles.value,
                  onOnClicked: _cache[2] || (_cache[2] = ($event: any) => (submitModal()))
                }, null, 8, ["buttonText", "disabled"]))
              : _createCommentVNode("", true)
        ])
      ]),
      _: 2
    }, [
      (showAdvancedView.value)
        ? {
            name: "controls",
            fn: _withCtx(() => [
              _createVNode(BadgeSquare, {
                "badge-text": "Advanced",
                size: "small"
              })
            ]),
            key: "0"
          }
        : undefined
    ]), 1032, ["title", "open"]),
    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(conflictFiles.value, (file) => {
      return (_openBlock(), _createBlock(FileConflictModal, {
        key: file.item.id,
        open: true,
        "disable-submit": false,
        directoryItem: file.item,
        onClose: () => removeFile(file),
        onReplaceItem: () => replaceFile(file),
        onSaveCopy: () => saveCopy(file)
      }, null, 8, ["directoryItem", "onClose", "onReplaceItem", "onSaveCopy"]))
    }), 128))
  ], 64))
}
}

})